import AdminInclusionTable from "@client/components/round/allocator/AdminInclusionTable";
import {
  InterestInstructions,
  ReviewProgressInstructions,
} from "@client/components/round/Instructions";
import ProposalLink from "@client/components/utils/ProposalLink";
import { Tooltip } from "@client/components/utils/Tooltip";
import DownChevron from "@client/icons/DownChevron";
import {
  getInterestDescription,
  getReviewProgressDescription,
  interestBg,
  isEffectivelyRecused,
  skimmedColumnColor,
  totalColumnColor,
} from "@client/utils/interest";
import { memberIsMeanRecommender } from "@client/utils/memberIsMeanRecommender";
import {
  proposalListSubtitle,
  proposalListTitle,
} from "@client/utils/proposalListTitle";
import { RecentIndicator } from "@client/utils/recentProposals";
import userIsAdmin from "@client/utils/userIsAdmin";
import {
  assertGetMember,
  getActiveProposals,
  getActiveRecommenders,
  getAllMembers,
  getEffectiveTrack,
  hasTrackWithMatchingStatus,
} from "@engine/round/accessors";
import type { Proposal, RoundMember } from "@engine/round/aggregate";
import type { Track } from "@lib/types";
import { useLocalStorage } from "@uidotdev/usehooks";
import clsx from "clsx";
import sum from "lodash/sum";
import { useContext, useId, useState } from "react";
import { Redirect } from "wouter";
import { handleSpreadsheetKeydown, partitionProposals } from "./Onboarding";
import { DispatchContext, StateContext } from "./Round";

export default function Investigations() {
  const context = useContext(StateContext);
  const { round, actingAsUserId: userId, mode } = context;
  const userRoundMember = assertGetMember(round, userId);
  const effectiveTrack = getEffectiveTrack(mode, userRoundMember);

  const [selectedTrack, setSelectedTrack] = useState<Track>(
    ["MAIN", "FREEDOM", "FAIRNESS"].includes(effectiveTrack)
      ? effectiveTrack
      : "MAIN",
  );

  const [instructionsVisible, setInstructionsVisible] = useLocalStorage(
    "instructionsVisible",
    true,
  );

  const {
    investigation,
    reviewProgress,
    proposalDisclosure: disclosures,
  } = round;

  const otherMembersToShow: RoundMember[] = Object.values(round.members).filter(
    (member) =>
      member.id !== userId &&
      !memberIsMeanRecommender(member) &&
      member.track === selectedTrack &&
      Object.values(member.evaluators).some(
        (evaluator) => evaluator.type === "recommender",
      ),
  );

  const userIsRecommender = getActiveRecommenders(round).find(
    (recommender) => recommender.memberId === userId,
  );

  const editable =
    Boolean(userIsRecommender) && effectiveTrack === selectedTrack;

  const proposals = getActiveProposals(round).filter(
    (proposal) => proposal.id !== round.holdProposalId,
  );
  const { inTrack, outOfTrack } = partitionProposals(proposals, selectedTrack);

  const freedomHasMembers = getAllMembers(round).some(
    (member) => member.track === "FREEDOM",
  );
  const fairnessHasMembers = getAllMembers(round).some(
    (member) => member.track === "FAIRNESS",
  );

  const completeInterests = proposals.filter(
    (proposal) =>
      investigation[`${proposal.id}%${userId}`]?.interest != null ||
      disclosures[`${proposal.id}%${userId}`]?.selfRecused,
  ).length;

  const completeReviews = proposals.filter(
    (proposal) =>
      reviewProgress[`${proposal.id}%${userId}`] != null ||
      disclosures[`${proposal.id}%${userId}`]?.selfRecused,
  ).length;

  const totalApplicants = proposals.length;

  const isAdmin = userIsAdmin(userId, context);

  const accessiblePhases = [
    "2.initial-evaluations",
    "3.full-discussion",
    "4.evals-freeze",
    "5.complete",
  ];

  if (
    !isAdmin &&
    !hasTrackWithMatchingStatus(round, userId, mode, accessiblePhases)
  ) {
    return <Redirect to="/" />;
  }

  return (
    <div className="w-screen h-screen overflow-y-auto p-4 pb-16">
      <h1 className="text-3xl font-bold mb-4">Investigations</h1>

      {userRoundMember.isRoundAdmin && (
        <details>
          <summary className="text-blue-500 cursor-pointer">
            Admin-only tool: who is included in the investigations table?
          </summary>
          <AdminInclusionTable selectedTrack={selectedTrack} />
        </details>
      )}

      {effectiveTrack === "NO_TRACK" &&
        (freedomHasMembers || fairnessHasMembers) && (
          <div>
            Track:
            <select
              value={selectedTrack}
              onChange={(e) => setSelectedTrack(e.target.value as Track)}
            >
              <option value="MAIN">Main</option>
              {freedomHasMembers && <option value="FREEDOM">Freedom</option>}
              {fairnessHasMembers && <option value="FAIRNESS">Fairness</option>}
            </select>
          </div>
        )}

      <button
        onClick={() => setInstructionsVisible(!instructionsVisible)}
        className="cursor-pointer"
      >
        <h2 className="text-2xl font-semibold">
          Instructions{" "}
          <DownChevron
            className={clsx(
              "inline w-5 h-5",
              instructionsVisible ? "" : "rotate-180",
            )}
          />
        </h2>
      </button>

      {instructionsVisible && (
        <div className="flex flex-col xl:flex-row gap-4">
          <InterestInstructions
            complete={completeInterests}
            total={totalApplicants}
            showTasks={effectiveTrack !== "NO_TRACK"}
          />
          <ReviewProgressInstructions
            complete={completeReviews}
            total={totalApplicants}
            showTasks={effectiveTrack !== "NO_TRACK"}
          />
        </div>
      )}

      {inTrack.length > 0 && (
        <>
          <h2 className="text-2xl font-semibold mt-6">
            {proposalListTitle(selectedTrack, true)}
          </h2>
          <h3 className="text-lg font-semibold">
            {proposalListSubtitle(selectedTrack, true)}
          </h3>
          <InterestTable
            proposals={inTrack}
            userRoundMember={userRoundMember}
            otherMembersToShow={otherMembersToShow}
            editable={editable}
          />
        </>
      )}

      {outOfTrack.length > 0 && (
        <>
          <h2 className="text-2xl font-semibold mt-6">
            {proposalListTitle(selectedTrack, false)}
          </h2>
          <h3 className="text-lg font-semibold">
            {proposalListSubtitle(selectedTrack, false)}
          </h3>
          <InterestTable
            proposals={outOfTrack}
            userRoundMember={userRoundMember}
            otherMembersToShow={otherMembersToShow}
            editable={editable}
          />
        </>
      )}
    </div>
  );
}

function InterestTable({
  proposals,
  userRoundMember,
  otherMembersToShow,
  editable,
}: {
  proposals: Proposal[];
  userRoundMember: RoundMember;
  otherMembersToShow: RoundMember[];
  editable: boolean;
}) {
  const { round, actingAsUserId: userId } = useContext(StateContext);
  const dispatch = useContext(DispatchContext);
  const idBase = useId();

  const {
    investigation,
    reviewProgress,
    proposalDisclosure: disclosures,
  } = round;

  const handleInterestChange = (proposalId: string, value: number | null) =>
    dispatch({
      type: "INVESTIGATION_INTEREST_CHANGE",
      proposalId,
      memberId: userId,
      interest: value,
    });

  const handleConfirmationChange = (proposalId: string, value: boolean) =>
    dispatch({
      type: "INVESTIGATION_INTEREST_CHANGE",
      proposalId,
      memberId: userId,
      confirmedReviewer: value,
    });

  const handleReviewProgressChange = (
    proposalId: string,
    value: number | null,
  ) =>
    dispatch({
      type: "REVIEW_PROGRESS_CHANGE",
      proposalId,
      memberId: userId,
      progress: value,
    });

  const allMembersIncludingSelf = [...otherMembersToShow];
  if (editable) {
    allMembersIncludingSelf.push(userRoundMember);
  }

  // "Number of recommenders who at least skimmed"
  const skimmedTotals = proposals.map((proposal) => {
    return sum(
      allMembersIncludingSelf.map((member) => {
        const progress = reviewProgress[`${proposal.id}%${member.id}`];
        return progress >= 1 ? 1 : 0;
      }),
    );
  });

  // "Investigation Interest Total"
  const interestRowTotals = proposals.map((proposal) => {
    return sum(
      allMembersIncludingSelf.map((member) => {
        const interest =
          investigation[`${proposal.id}%${member.id}`]?.interest || 0;
        const disclosure = disclosures[`${proposal.id}%${member.id}`];
        const isRecused = isEffectivelyRecused(disclosure);
        return isRecused ? 0 : interest;
      }),
    );
  });

  // "Total Review Progress"
  const reviewRowTotals = proposals.map((proposal) => {
    return sum(
      allMembersIncludingSelf.map((member) => {
        const review = reviewProgress[`${proposal.id}%${member.id}`] || 0;
        const disclosure = disclosures[`${proposal.id}%${member.id}`];
        const isRecused = isEffectivelyRecused(disclosure);
        return isRecused ? 0 : review;
      }),
    );
  });

  // (for other reviewers) "Investigation Interest" column total
  const interestColumnTotals = otherMembersToShow.map((member) => {
    return sum(
      proposals.map((proposal) => {
        const interest =
          investigation[`${proposal.id}%${member.id}`]?.interest || 0;
        const disclosure = disclosures[`${proposal.id}%${member.id}`];
        const isRecused = isEffectivelyRecused(disclosure);
        return isRecused ? 0 : interest;
      }),
    );
  });

  // (for other reviewers) "Review Progress" column total
  const reviewColumnTotals = otherMembersToShow.map((member) => {
    return sum(
      proposals.map((proposal) => {
        const review = reviewProgress[`${proposal.id}%${member.id}`] || 0;
        const disclosure = disclosures[`${proposal.id}%${member.id}`];
        const isRecused = isEffectivelyRecused(disclosure);
        return isRecused ? 0 : review;
      }),
    );
  });

  // (for the user) "Investigation Interest" column total
  const userInterestColumnTotal = sum(
    proposals.map((proposal) => {
      const interest = investigation[`${proposal.id}%${userId}`]?.interest || 0;
      const disclosure = disclosures[`${proposal.id}%${userId}`];
      const isRecused = isEffectivelyRecused(disclosure);
      return isRecused ? 0 : interest;
    }),
  );

  // (for the user) "Review Progress" column total
  const userReviewColumnTotal = sum(
    proposals.map((proposal) => {
      const review = reviewProgress[`${proposal.id}%${userId}`] || 0;
      const disclosure = disclosures[`${proposal.id}%${userId}`];
      const isRecused = isEffectivelyRecused(disclosure);
      return isRecused ? 0 : review;
    }),
  );

  return (
    <div className="overflow-auto max-h-full max-w-full p4 mt-4 mb-40">
      <table className="light:bg-white dark:bg-stone-800 border-separate border-spacing-0">
        <thead className="sticky top-0 z-20">
          <tr>
            <th className="py-2 px-4 text-left border light:border-gray-200 dark:border-stone-700 light:bg-white dark:bg-stone-800">
              Proposal
            </th>
            <th
              colSpan={otherMembersToShow.length + (editable ? 3 : 2)}
              className="py-2 px-4 border-r border-y light:border-gray-200 dark:border-stone-700 light:bg-white dark:bg-stone-800"
            >
              Investigation Interest
            </th>
            <th
              className="px-4 light:bg-stone-100 dark:bg-[#181818]" // separator
              rowSpan={3}
            />
            <th
              colSpan={otherMembersToShow.length + (editable ? 3 : 2)}
              className="py-2 px-4 border-x border-y light:border-gray-200 dark:border-stone-700 light:bg-white dark:bg-stone-800"
            >
              Review Progress
            </th>
          </tr>
          <tr>
            <th
              className="border-x border-b light:border-gray-200 dark:border-stone-700 light:bg-white dark:bg-stone-800"
              rowSpan={2}
            />
            {editable && (
              <th className="py-2 px-4 border-r border-b light:border-gray-200 dark:border-stone-700 light:bg-white dark:bg-stone-800">
                {userRoundMember.initials}
              </th>
            )}
            {otherMembersToShow.map((member) => (
              <th
                key={member.id}
                className="py-2 px-4 border-r border-b light:border-gray-200 dark:border-stone-700 light:bg-white dark:bg-stone-800"
              >
                {member.initials}
              </th>
            ))}
            <th className="py-2 px-4 border-r border-b light:border-gray-200 dark:border-stone-700 light:bg-white dark:bg-stone-800">
              Total
            </th>
            <th className="py-2 px-4 border-r border-b light:border-gray-200 dark:border-stone-700 light:bg-white dark:bg-stone-800">
              Confirmed Reviewers
            </th>
            {editable && (
              <th className="py-2 px-4 border-x border-b light:border-gray-200 dark:border-stone-700 light:bg-white dark:bg-stone-800">
                {userRoundMember.initials}
              </th>
            )}
            {otherMembersToShow.map((member, i) => (
              <th
                key={member.id}
                className={clsx(
                  "py-2 px-4 border-r border-b light:border-gray-200 dark:border-stone-700 light:bg-white dark:bg-stone-800",
                  !editable && i === 0 && "border-l",
                )}
              >
                {member.initials}
              </th>
            ))}
            <th
              className={clsx(
                "py-2 px-4 border-r border-b light:border-gray-200 dark:border-stone-700 light:bg-white dark:bg-stone-800",
                !editable && otherMembersToShow.length === 0 && "border-l",
              )}
            >
              <div className="max-w-30 wrap">
                Number of recommenders who at least skimmed
              </div>
            </th>
            <th className="py-2 px-4 border-r border-b light:border-gray-200 dark:border-stone-700 light:bg-white dark:bg-stone-800">
              <div className="max-w-24 wrap">Total Review Progress</div>
            </th>
          </tr>
          <tr>
            {editable && (
              <td className="py-2 px-4 border-r border-b light:border-gray-200 dark:border-stone-700 light:bg-white dark:bg-stone-800">
                {userInterestColumnTotal}
              </td>
            )}
            {interestColumnTotals.map((total, index) => (
              <td
                key={index}
                className="py-2 px-4 border-r border-b light:border-gray-200 dark:border-stone-700 light:bg-white dark:bg-stone-800"
              >
                {total}
              </td>
            ))}
            <td // Total column
              className="py-2 px-4 border-r border-b light:border-gray-200 dark:border-stone-700 light:bg-white dark:bg-stone-800"
            />
            <td // Confirmed Reviewers column
              className="py-2 px-4 border-r border-b light:border-gray-200 dark:border-stone-700 light:bg-white dark:bg-stone-800"
            />
            {editable && (
              <td className="py-2 px-4 border-x border-b light:border-gray-200 dark:border-stone-700 light:bg-white dark:bg-stone-800">
                {userReviewColumnTotal}
              </td>
            )}
            {reviewColumnTotals.map((total, index) => (
              <td
                key={index}
                className={clsx(
                  "py-2 px-4 border-r border-b light:border-gray-200 dark:border-stone-700 light:bg-white dark:bg-stone-800",
                  !editable && index === 0 && "border-l",
                )}
              >
                {total}
              </td>
            ))}
            <td
              className={clsx(
                "py-2 px-4 border-r border-b light:border-gray-200 dark:border-stone-700 light:bg-white dark:bg-stone-800",
                !editable && reviewColumnTotals.length === 0 && "border-l",
              )}
            >
              {sum(skimmedTotals)}
            </td>
            <td className="py-2 px-4 border-r border-b light:border-gray-200 dark:border-stone-700 light:bg-white dark:bg-stone-800">
              {sum(reviewRowTotals)}
            </td>
          </tr>
        </thead>
        <tbody>
          {proposals.map((proposal, index) => {
            const interest =
              investigation[`${proposal.id}%${userId}`]?.interest;
            const progress = reviewProgress[`${proposal.id}%${userId}`];
            const interestInputId = `${idBase}-interest-${index}`;
            const reviewProgressInputId = `${idBase}-review-progress-${index}`;
            const disclosure = disclosures[`${proposal.id}%${userId}`];
            const isRecused = isEffectivelyRecused(disclosure);
            const membersToInclude = [...otherMembersToShow];
            if (editable) {
              membersToInclude.push(userRoundMember);
            }
            const confirmedReviewers = membersToInclude.filter((member) => {
              const disclosure = disclosures[`${proposal.id}%${member.id}`];
              const isRecused = isEffectivelyRecused(disclosure);
              return (
                !isRecused &&
                investigation[`${proposal.id}%${member.id}`]?.confirmedReviewer
              );
            });
            const selfConfirmedReviewer =
              investigation[`${proposal.id}%${userId}`]?.confirmedReviewer;

            return (
              <tr key={proposal.id} className="text-sm">
                <th className="py-2 px-4 border-x border-b light:border-gray-200 dark:border-stone-700 sticky left-0 light:bg-white dark:bg-stone-800 text-left z-10">
                  <ProposalLink proposal={proposal}>
                    {proposal.shortname}
                    <RecentIndicator proposalId={proposal.id} />
                  </ProposalLink>
                </th>
                {editable && (
                  <td
                    className={clsx(
                      "py-2 px-4 border-r border-b light:border-gray-200 dark:border-stone-700",
                      !isRecused && interestBg(interest, true),
                    )}
                  >
                    <div className="flex items-center gap-2">
                      <Tooltip
                        title={getInterestDescription(interest, isRecused)}
                      >
                        <input
                          className="w-16 p-1 dark:border-stone-700"
                          id={interestInputId}
                          placeholder="[0-4]"
                          type="text"
                          autoComplete="off"
                          value={
                            isRecused ? "Rec" : interest == null ? "" : interest
                          }
                          onChange={async (e) => {
                            const value = e.target.value.slice(-1);
                            const parsedValue = parseInt(value);
                            if (
                              value === "" ||
                              (parsedValue >= 0 && parsedValue <= 4)
                            ) {
                              await handleInterestChange(
                                proposal.id,
                                parsedValue,
                              );
                              setTimeout(() => {
                                document
                                  .getElementById(interestInputId)
                                  ?.focus();
                              }, 0);
                            }
                          }}
                          onKeyDown={(e) =>
                            handleSpreadsheetKeydown(
                              (index: number) => `${idBase}-interest-${index}`,
                              proposals,
                              handleInterestChange,
                              e,
                              index,
                            )
                          }
                        />
                      </Tooltip>
                    </div>
                  </td>
                )}
                {otherMembersToShow.map((member) => {
                  const interest =
                    investigation[`${proposal.id}%${member.id}`]?.interest;
                  return (
                    <td
                      key={member.id}
                      className={clsx(
                        "py-2 px-4 border-r border-b light:border-gray-200 dark:border-stone-700",
                        interestBg(interest),
                      )}
                    >
                      {interest}
                    </td>
                  );
                })}
                <td
                  className={clsx(
                    "py-2 px-4 border-r border-b light:border-gray-200 dark:border-stone-700",
                    totalColumnColor(
                      interestRowTotals[index],
                      otherMembersToShow.length + 1,
                    ),
                  )}
                >
                  {interestRowTotals[index]}
                </td>
                <td className="p-1 border-r border-b light:border-gray-200 dark:border-stone-700">
                  <div className="flex flex-col gap-2">
                    <div className="flex flex-row gap-2">
                      {confirmedReviewers.map((member) => (
                        <div key={member.id}>{member.initials}</div>
                      ))}
                    </div>
                    {editable && (
                      <div>
                        <Tooltip
                          title={
                            isRecused
                              ? "Disabled because you are recused on this proposal."
                              : ""
                          }
                        >
                          <span>
                            <button
                              className="cursor-pointer text-sm bg-white hover:bg-gray-100 text-black font-semibold py-1 px-2 border border-gray-300 rounded-md shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                              onClick={() =>
                                handleConfirmationChange(
                                  proposal.id,
                                  !selfConfirmedReviewer,
                                )
                              }
                              disabled={isRecused}
                            >
                              {isRecused || !selfConfirmedReviewer
                                ? "Claim"
                                : "Unclaim"}
                            </button>
                          </span>
                        </Tooltip>
                      </div>
                    )}
                  </div>
                </td>
                <td
                  className="py-2 px-4 light:bg-stone-100 dark:bg-[#181818]" // separator
                />
                {editable && (
                  <td
                    className={clsx(
                      "py-2 px-4 border-x border-b light:border-gray-200 dark:border-stone-700",
                      !isRecused && interestBg(progress, true),
                    )}
                  >
                    <div className="flex items-center gap-2">
                      <Tooltip
                        title={getReviewProgressDescription(
                          progress,
                          isRecused,
                        )}
                      >
                        <input
                          className="w-16 p-1 dark:border-stone-700"
                          id={reviewProgressInputId}
                          placeholder="[1-4]"
                          type="text"
                          autoComplete="off"
                          value={
                            isRecused ? "Rec" : progress == null ? "" : progress
                          }
                          onChange={async (e) => {
                            const value = e.target.value.slice(-1);
                            const parsedValue = parseInt(value);
                            if (
                              value === "" ||
                              (parsedValue >= 1 && parsedValue <= 4)
                            ) {
                              await handleReviewProgressChange(
                                proposal.id,
                                parsedValue,
                              );
                              setTimeout(() => {
                                document
                                  .getElementById(reviewProgressInputId)
                                  ?.focus();
                              }, 0);
                            }
                          }}
                          onKeyDown={(e) =>
                            handleSpreadsheetKeydown(
                              (index: number) =>
                                `${idBase}-review-progress-${index}`,
                              proposals,
                              handleReviewProgressChange,
                              e,
                              index,
                            )
                          }
                        />
                      </Tooltip>
                    </div>
                  </td>
                )}
                {otherMembersToShow.map((member, i) => {
                  const progress =
                    reviewProgress[`${proposal.id}%${member.id}`];
                  const disclosure = disclosures[`${proposal.id}%${member.id}`];
                  const isRecused = isEffectivelyRecused(disclosure);

                  return (
                    <td
                      key={member.id}
                      className={clsx(
                        "py-2 px-4 border-r border-b light:border-gray-200 dark:border-stone-700",
                        !editable && i === 0 && "border-l",
                        interestBg(progress, isRecused),
                      )}
                    >
                      {isRecused ? "Rec" : progress}
                    </td>
                  );
                })}
                <td // number of recommenders who at least skimmed
                  className={clsx(
                    "py-2 px-4 border-r border-b light:border-gray-200 dark:border-stone-700",
                    !editable && otherMembersToShow.length === 0 && "border-l",
                    skimmedColumnColor(
                      skimmedTotals[index],
                      otherMembersToShow.length + 1,
                    ),
                  )}
                >
                  {skimmedTotals[index]}
                </td>
                <td
                  className={clsx(
                    "py-2 px-4 border-r border-b light:border-gray-200 dark:border-stone-700",
                    totalColumnColor(
                      reviewRowTotals[index],
                      otherMembersToShow.length + 1,
                    ),
                  )}
                >
                  {reviewRowTotals[index]}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
